import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogoWinarq } from "../components/LogoWinarq";
import { collection, getDocs, query, where, limit } from "firebase/firestore";
import { db } from "../firebase/config";

// ── Paleta y tipografía ───────────────────────────────────────────────────────
const O = "#D2611A", OL = "#E8752A", BK = "#1C1917", BK2 = "#0F0D0C";
const W = {
  bg:"#FAF9F7", s1:"#FFFFFF", s2:"#F4F2EF", border:"#E8E4DE",
  off:"#3D3830", muted:"#8C8078", dim:"#C8C0B8",
  sans:"'Outfit','Helvetica Neue',Arial,sans-serif",
  mono:"'IBM Plex Mono',monospace",
};

const FONTS = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@200;300;400;500;600;700;800&family=IBM+Plex+Mono:wght@300;400;500&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
html{scroll-behavior:smooth}
body{-webkit-font-smoothing:antialiased;overflow-x:hidden}

@keyframes marquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}
@keyframes fadeUp{from{opacity:0;transform:translateY(30px)}to{opacity:1;transform:translateY(0)}}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
@keyframes pulse{0%,100%{opacity:1}50%{opacity:.3}}
@keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
@keyframes lineGrow{from{width:0}to{width:100%}}
@keyframes countUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}

@media(prefers-reduced-motion:reduce){
  *{animation:none!important;transition:none!important}
}
@media(max-width:900px){
  .hero-grid{grid-template-columns:1fr!important}
  .hero-right{display:none!important}
  .steps-grid{grid-template-columns:1fr 1fr!important}
  .why-grid{grid-template-columns:1fr!important}
  .test-grid{grid-template-columns:1fr!important}
  .contact-grid{grid-template-columns:1fr!important}
  .footer-grid{grid-template-columns:1fr 1fr!important}
  .nav-links{display:none!important}
  section,.pad{padding-left:20px!important;padding-right:20px!important}
}
@media(max-width:600px){
  .steps-grid{grid-template-columns:1fr!important}
  .footer-grid{grid-template-columns:1fr!important}
}
`;

// ── Hook: scroll-triggered reveal ─────────────────────────────────────────────
function useReveal(threshold = 0.08) {
  const ref = useRef();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

// ── Hook: parallax on scroll ──────────────────────────────────────────────────
function useParallax(speed = 0.3) {
  const ref = useRef();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => {
      const rect = el.getBoundingClientRect();
      const center = window.innerHeight / 2;
      const offset = (rect.top + rect.height / 2 - center) * speed;
      el.style.transform = `translateY(${offset}px)`;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [speed]);
  return ref;
}

// ── Componente Reveal ─────────────────────────────────────────────────────────
function Reveal({ children, delay = 0, y = 30, style = {} }) {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? "none" : `translateY(${y}px)`,
      transition: `opacity .7s ${delay}s ease, transform .7s ${delay}s ease`,
      ...style
    }}>
      {children}
    </div>
  );
}

// ── Número animado al hacer scroll ────────────────────────────────────────────
function AnimatedNumber({ value, suffix = "", prefix = "" }) {
  const [ref, visible] = useReveal();
  const [display, setDisplay] = useState("0");
  useEffect(() => {
    if (!visible) return;
    const num = parseFloat(value);
    if (isNaN(num)) { setDisplay(value); return; }
    const dur = 1200, steps = 40;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const ease = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(num * ease).toLocaleString("es-AR"));
      if (step >= steps) clearInterval(timer);
    }, dur / steps);
    return () => clearInterval(timer);
  }, [visible, value]);
  return (
    <span ref={ref}>{prefix}{display}{suffix}</span>
  );
}

// ── Fotos del hero con parallax ───────────────────────────────────────────────
function HeroFotos() {
  const [fotos, setFotos] = useState([]);
  const [idx, setIdx] = useState(0);
  const [fading, setFading] = useState(false);
  const parallaxRef = useParallax(0.2);

  useEffect(() => {
    getDocs(query(collection(db,"productos"), where("publicado","==",true), limit(8)))
      .then(snap => {
        const imgs = snap.docs.map(d=>({url:d.data().imagen_url,rubro:d.data().rubro,nombre:d.data().nombre})).filter(p=>p.url);
        setFotos(imgs.length > 0 ? imgs : FALLBACK_FOTOS);
      }).catch(()=>setFotos(FALLBACK_FOTOS));
  }, []);

  useEffect(() => {
    if (fotos.length <= 1) return;
    const t = setInterval(() => {
      setFading(true);
      setTimeout(() => { setIdx(i=>(i+1)%fotos.length); setFading(false); }, 600);
    }, 4500);
    return () => clearInterval(t);
  }, [fotos]);

  const foto = fotos[idx];
  return (
    <div ref={parallaxRef} style={{position:"relative",width:"100%",height:"100%",overflow:"hidden"}}>
      {foto && (
        <div style={{
          position:"absolute",inset:0,
          backgroundImage:`url('${foto.url}')`,
          backgroundSize:"contain",
          backgroundPosition:"center",
          backgroundRepeat:"no-repeat",
          opacity: fading ? 0 : 1,
          transition:"opacity .6s ease",
        }}/>
      )}
      {/* Overlay gradiente */}
      <div style={{position:"absolute",inset:0,background:"linear-gradient(to right, rgba(28,25,23,.15) 0%, transparent 60%)"}}/>
      {foto && (
        <div style={{position:"absolute",bottom:16,right:16,background:"rgba(28,25,23,.8)",backdropFilter:"blur(8px)",padding:"7px 14px"}}>
          <div style={{fontFamily:W.mono,fontSize:7,color:O,letterSpacing:2,textTransform:"uppercase",marginBottom:2}}>{foto.rubro}</div>
          <div style={{fontFamily:W.sans,fontSize:10,color:"#fff",fontWeight:600,maxWidth:200,lineHeight:1.3}}>{foto.nombre}</div>
        </div>
      )}
      {/* Dots */}
      {fotos.length>1&&(
        <div style={{position:"absolute",bottom:16,left:16,display:"flex",gap:5}}>
          {fotos.map((_,i)=>(
            <div key={i} onClick={()=>setIdx(i)} style={{width:i===idx?18:5,height:5,background:i===idx?O:"rgba(255,255,255,.4)",borderRadius:3,cursor:"pointer",transition:"all .3s"}}/>
          ))}
        </div>
      )}
    </div>
  );
}

const FALLBACK_FOTOS = [
  {url:"https://images.unsplash.com/photo-1556909172-54557c7e4fb7?w=900&q=80",rubro:"Cocinas",nombre:"Cocina integral MDF lacado"},
  {url:"https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=900&q=80",rubro:"Sanitarios",nombre:"Inodoro rimless premium"},
  {url:"https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=900&q=80",rubro:"Pisos SPC",nombre:"Piso flotante SPC 5mm"},
];

// ── Carrusel horizontal de productos ─────────────────────────────────────────
function CarruselProductos({ navigate }) {
  const [productos, setProductos] = useState([]);
  const [idx, setIdx] = useState(0);
  const [auto, setAuto] = useState(true);
  const [ref, visible] = useReveal(0.05);

  useEffect(() => {
    getDocs(query(collection(db,"productos"),where("publicado","==",true),limit(12)))
      .then(snap => setProductos(snap.docs.map(d=>({id:d.id,...d.data()}))))
      .catch(()=>{});
  }, []);

  useEffect(() => {
    if (!auto || productos.length === 0) return;
    const t = setInterval(()=>setIdx(i=>(i+1)%Math.max(1,productos.length-3)),3800);
    return ()=>clearInterval(t);
  }, [auto, productos]);

  const VISIBLE = 4;
  const max = Math.max(0, productos.length - VISIBLE);

  return (
    <div ref={ref} style={{opacity:visible?1:0,transform:visible?"none":"translateY(30px)",transition:"all .7s ease"}}>
      <div style={{display:"flex",gap:12,overflowX:"auto",scrollSnapType:"x mandatory",WebkitOverflowScrolling:"touch",paddingBottom:8,scrollbarWidth:"none"}}>
        {productos.map((p,i)=>{
          const precio = p.precio_venta;
          const precioIVA = precio ? (precio*1.21).toFixed(2) : null;
          const delay = i * 0.05;
          return (
            <div key={p.id} onClick={()=>navigate(`/catalogo?rubro=${encodeURIComponent(p.rubro||"")}`)}
              style={{flexShrink:0,width:220,scrollSnapAlign:"start",cursor:"pointer",background:W.s1,border:`1.5px solid ${W.border}`,overflow:"hidden",transition:"border-color .2s,box-shadow .2s",animation:`fadeUp .5s ${delay}s both`}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=O;e.currentTarget.style.boxShadow=`0 8px 24px rgba(210,97,26,.12)`;}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=W.border;e.currentTarget.style.boxShadow="none";}}>
              <div style={{height:160,overflow:"hidden",background:W.s2,display:"flex",alignItems:"center",justifyContent:"center"}}>
                {p.imagen_url
                  ? <img src={p.imagen_url} alt={p.nombre} style={{maxWidth:"100%",maxHeight:160,objectFit:"contain",transition:"transform .4s"}} onMouseOver={e=>e.target.style.transform="scale(1.05)"} onMouseOut={e=>e.target.style.transform="scale(1)"}/>
                  : <div style={{fontFamily:W.sans,fontSize:10,color:W.muted}}>Sin imagen</div>
                }
              </div>
              <div style={{padding:"12px 14px"}}>
                <div style={{fontFamily:W.sans,fontSize:8,letterSpacing:2,textTransform:"uppercase",color:O,marginBottom:3,fontWeight:600}}>{p.rubro}</div>
                <div style={{fontFamily:W.sans,fontSize:13,color:BK,fontWeight:700,lineHeight:1.25,marginBottom:6}}>{p.nombre}</div>
                {precioIVA
                  ? <div style={{fontFamily:W.mono,fontSize:11,color:O,fontWeight:600}}>USD {Number(precioIVA).toLocaleString("es-AR",{minimumFractionDigits:2})} c/IVA</div>
                  : <div style={{fontFamily:W.sans,fontSize:9,color:W.muted,letterSpacing:1,textTransform:"uppercase"}}>Cotización personalizada</div>
                }
              </div>
              <div style={{height:2,background:O,transform:"scaleX(0)",transformOrigin:"left",transition:"transform .3s"}} onMouseEnter={e=>e.target.style.transform="scaleX(1)"} onMouseLeave={e=>e.target.style.transform="scaleX(0)"}/>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Testimonios ───────────────────────────────────────────────────────────────
const TESTS = [
  {q:"Importamos sanitarios para 24 unidades. El precio fue significativamente menor al distribuidor local. La calidad superó lo que habíamos comprado antes en Argentina.",a:"Ing. M.S.",o:"Torre residencial · 24 unidades · Buenos Aires"},
  {q:"Lo que más valoro es la aprobación previa de los shop drawings. Con 60 aberturas a medida no hay margen para errores. Llegó exactamente como especificamos.",a:"Arq. V.R.",o:"Complejo residencial · 12 unidades · GBA Norte"},
  {q:"Primera importación directa. Todo llegó en plazo y la factura individual por unidad simplificó el cierre contable. Ya coordinamos el segundo ciclo.",a:"Cr. D.F.",o:"Edificio en altura · 16 unidades · GBA"},
  {q:"La diferencia de precio fue muy importante para el proyecto. Pudimos mejorar la calidad de los materiales sin salir del presupuesto.",a:"Ing. P.L.",o:"Desarrollo residencial · Córdoba"},
  {q:"Excelente desde la cotización hasta la entrega. Todo claro, sin costos ocultos. Ya tenemos el tercer pedido en proceso.",a:"Arq. R.G.",o:"Estudio de arquitectura · Rosario"},
];

// ── LANDING ───────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [navOpaque, setNavOpaque] = useState(false);
  const [form, setForm] = useState({nombre:"",empresa:"",whatsapp:"",email:"",rubro:"Pisos SPC",proyecto:""});
  const [enviado, setEnviado] = useState(false);
  const [testIdx, setTestIdx] = useState(0);

  useEffect(()=>{
    const fn = () => {
      setScrollY(window.scrollY);
      setNavOpaque(window.scrollY > 60);
    };
    window.addEventListener("scroll", fn, {passive:true});
    return ()=>window.removeEventListener("scroll", fn);
  },[]);

  // Rotar testimonios
  useEffect(()=>{
    const t = setInterval(()=>setTestIdx(i=>(i+1)%TESTS.length), 5000);
    return ()=>clearInterval(t);
  },[]);

  const go = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    window.scrollTo({top:el.getBoundingClientRect().top+window.scrollY-20,behavior:"smooth"});
  };

  const BP = {
    padding:"12px 28px",background:O,color:"#fff",
    fontFamily:W.sans,fontSize:10,letterSpacing:2,textTransform:"uppercase",
    border:"none",cursor:"pointer",fontWeight:700,transition:"all .2s",
  };
  const BS = {
    padding:"12px 22px",background:"transparent",color:W.off,
    fontFamily:W.sans,fontSize:10,letterSpacing:2,textTransform:"uppercase",
    border:`1.5px solid ${W.border}`,cursor:"pointer",fontWeight:500,transition:"all .2s",
  };
  const INP = {
    width:"100%",background:"transparent",border:"none",
    borderBottom:`1.5px solid ${W.border}`,color:BK,
    fontFamily:W.sans,fontSize:13,padding:"9px 0",outline:"none",
  };

  // Efecto parallax en el hero
  const heroParallax = { transform: `translateY(${scrollY * 0.15}px)` };

  return (
    <div style={{background:W.bg,color:BK,fontFamily:W.sans,overflowX:"hidden"}}>
      <style>{FONTS}</style>

      {/* ── NAV ── */}
      <nav style={{
        position:"fixed",top:0,left:0,right:0,height:64,zIndex:200,
        background:navOpaque?"rgba(250,249,247,.96)":"transparent",
        backdropFilter:navOpaque?"blur(12px)":"none",
        borderBottom:navOpaque?`1px solid ${W.border}`:"none",
        display:"flex",alignItems:"center",padding:"0 40px",gap:32,
        transition:"all .4s ease",
      }}>
        <div style={{marginRight:"auto",cursor:"pointer"}} onClick={()=>go("hero")}>
          <LogoWinarq height={44} dark={true}/>
        </div>
        <div className="nav-links" style={{display:"flex",gap:4}}>
          {[["como","Cómo funciona"],["catalogo","Catálogo"],["precios","Por qué elegirnos"],["contacto","Cotizar"]].map(([id,l])=>(
            <button key={id} onClick={()=>go(id)} style={{padding:"0 14px",background:"transparent",border:"none",fontFamily:W.sans,fontSize:10,letterSpacing:1.5,textTransform:"uppercase",color:navOpaque?W.muted:"rgba(28,25,23,.7)",cursor:"pointer",fontWeight:500,height:64,transition:"color .2s"}}
              onMouseEnter={e=>e.currentTarget.style.color=O}
              onMouseLeave={e=>e.currentTarget.style.color=navOpaque?W.muted:"rgba(28,25,23,.7)"}>
              {l}
            </button>
          ))}
        </div>
        <button style={{...BS,fontSize:9,padding:"7px 18px",borderColor:navOpaque?W.border:"rgba(28,25,23,.3)"}} onClick={()=>navigate("/auth")}
          onMouseEnter={e=>{e.currentTarget.style.borderColor=O;e.currentTarget.style.color=O;}}
          onMouseLeave={e=>{e.currentTarget.style.borderColor=navOpaque?W.border:"rgba(28,25,23,.3)";e.currentTarget.style.color=W.off;}}>
          Acceso clientes
        </button>
        <button style={{...BP,fontSize:9,padding:"8px 20px"}} onClick={()=>go("contacto")}
          onMouseEnter={e=>e.currentTarget.style.background=OL}
          onMouseLeave={e=>e.currentTarget.style.background=O}>
          Cotizar obra →
        </button>
      </nav>

      {/* ── HERO ── */}
      <section id="hero" style={{minHeight:"100vh",display:"grid",gridTemplateColumns:"1fr 1fr"}} className="hero-grid">
        {/* LEFT */}
        <div style={{display:"flex",flexDirection:"column",justifyContent:"center",padding:"80px 52px 60px 40px",borderRight:`1px solid ${W.border}`,position:"relative",overflow:"hidden"}}>

          {/* Línea decorativa animada */}
          <div style={{position:"absolute",top:0,left:0,width:3,background:`linear-gradient(to bottom, transparent, ${O}, transparent)`,height:`${Math.min(100,scrollY/3)}%`,transition:"height .1s"}}/>

          <div style={{...heroParallax}}>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:32,animation:"fadeUp .8s .1s both"}}>
              <div style={{width:40,height:3,background:O}}/>
              <span style={{fontFamily:W.sans,fontSize:9,color:O,letterSpacing:2.5,textTransform:"uppercase",fontWeight:600}}>Importación directa · China → Argentina</span>
            </div>

            <h1 style={{fontFamily:W.sans,fontWeight:800,lineHeight:1.0,color:BK,marginBottom:20,animation:"fadeUp .8s .2s both"}}>
              <span style={{fontSize:"clamp(36px,5vw,72px)",display:"block"}}>El mismo</span>
              <span style={{fontSize:"clamp(36px,5vw,72px)",display:"block"}}>material.</span>
              <span style={{fontSize:"clamp(36px,5vw,72px)",display:"block",color:O}}>Un precio</span>
              <span style={{fontSize:"clamp(36px,5vw,72px)",display:"block",fontWeight:200,color:W.muted}}>diferente.</span>
            </h1>

            <p style={{fontFamily:W.sans,fontSize:14,color:W.off,maxWidth:440,lineHeight:1.85,marginBottom:36,fontWeight:300,animation:"fadeUp .8s .4s both"}}>
              Consolidamos los pedidos de arquitectos y desarrolladores en un solo contenedor. Precio de fábrica entregado en Buenos Aires — sin intermediarios, sin sorpresas.
            </p>

            {/* Stats con números animados */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:36,animation:"fadeUp .8s .5s both"}}>
              {[
                {n:"40",suf:"+",label:"Años de relaciones con fábricas"},
                {n:"20",suf:"–30%",label:"Ahorro promedio sobre precio local"},
                {n:"60",suf:"+",label:"Fábricas certificadas en China"},
                {n:"4",suf:" ciclos",label:"Contenedores consolidados por año"},
              ].map((s,i)=>(
                <div key={i} style={{padding:"14px 16px",background:W.s1,border:`1px solid ${W.border}`}}>
                  <div style={{fontFamily:W.sans,fontSize:28,fontWeight:800,color:O,lineHeight:1,marginBottom:4}}>
                    <AnimatedNumber value={s.n} suffix={s.suf}/>
                  </div>
                  <div style={{fontFamily:W.sans,fontSize:10,color:W.muted,fontWeight:300,lineHeight:1.4}}>{s.label}</div>
                </div>
              ))}
            </div>

            <div style={{display:"flex",gap:12,flexWrap:"wrap",animation:"fadeUp .8s .6s both"}}>
              <button style={BP} onClick={()=>go("contacto")}
                onMouseEnter={e=>e.currentTarget.style.background=OL}
                onMouseLeave={e=>e.currentTarget.style.background=O}>
                Cotizar mi obra →
              </button>
              <button style={BS} onClick={()=>navigate("/catalogo")}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=O;e.currentTarget.style.color=O;}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=W.border;e.currentTarget.style.color=W.off;}}>
                Ver catálogo
              </button>
            </div>
          </div>
        </div>

        {/* RIGHT — fotos con parallax */}
        <div className="hero-right" style={{position:"relative",overflow:"hidden",minHeight:"100vh"}}>
          <HeroFotos/>
          {/* Gradiente bottom */}
          <div style={{position:"absolute",bottom:0,left:0,right:0,height:120,background:`linear-gradient(to top, ${W.bg}, transparent)`}}/>
        </div>
      </section>

      {/* ── TICKER ── */}
      <div style={{borderTop:`1px solid ${W.border}`,borderBottom:`1px solid ${W.border}`,background:BK,padding:"11px 0",overflow:"hidden"}}>
        <div style={{display:"flex",animation:"marquee 30s linear infinite",whiteSpace:"nowrap"}}>
          {[...Array(2)].map((_,i)=>(
            <div key={i} style={{display:"flex",paddingRight:60}}>
              {["Sanitarios","Pisos SPC","Hornos y Anafes","Puertas WPC","Carpintería","Cocinas integrales","Bañeras","Vanitorys","Barandas","Importación directa","Sin intermediarios","Precio de fábrica"].map(t=>(
                <span key={t} style={{fontFamily:W.sans,fontSize:9,letterSpacing:2.5,textTransform:"uppercase",color:"rgba(255,255,255,.35)",marginRight:48,fontWeight:500}}>
                  {t}<span style={{color:O,margin:"0 16px"}}>·</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ── CATÁLOGO — carrusel de productos ── */}
      <section id="catalogo" className="pad" style={{padding:"80px 40px",background:W.bg}}>
        <Reveal style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:32}}>
          <div>
            <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:8}}>
              <div style={{width:32,height:3,background:O}}/>
              <span style={{fontFamily:W.sans,fontSize:9,color:O,letterSpacing:2.5,textTransform:"uppercase",fontWeight:600}}>Catálogo</span>
            </div>
            <h2 style={{fontFamily:W.sans,fontSize:"clamp(22px,3vw,40px)",fontWeight:800,color:BK}}>Productos importados</h2>
            <p style={{fontFamily:W.sans,fontSize:13,color:W.muted,marginTop:6,fontWeight:300}}>Fotos reales · Precios con IVA para clientes registrados</p>
          </div>
          <button style={{...BS,fontSize:9,padding:"8px 18px"}} onClick={()=>navigate("/catalogo")}
            onMouseEnter={e=>{e.currentTarget.style.borderColor=O;e.currentTarget.style.color=O;}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor=W.border;e.currentTarget.style.color=W.off;}}>
            Ver todo →
          </button>
        </Reveal>
        <CarruselProductos navigate={navigate}/>
      </section>

      {/* ── POR QUÉ CONVIENE ── */}
      <section id="precios" style={{background:BK,padding:"80px 40px"}} className="pad">
        <Reveal style={{marginBottom:52}}>
          <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:8}}>
            <div style={{width:32,height:3,background:O}}/>
            <span style={{fontFamily:W.sans,fontSize:9,color:O,letterSpacing:2.5,textTransform:"uppercase",fontWeight:600}}>Por qué elegirnos</span>
          </div>
          <h2 style={{fontFamily:W.sans,fontSize:"clamp(22px,3vw,40px)",fontWeight:800,color:"#FAF9F7"}}>
            La diferencia<br/><span style={{fontWeight:200,color:"rgba(255,255,255,.4)"}}>está en el acceso.</span>
          </h2>
        </Reveal>
        <div className="why-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:2}}>
          {[
            {icon:"↓",t:"Hasta 40% menos en materiales",d:"Al consolidar pedidos en un solo contenedor accedés al precio de importador directo. Sin cadena de distribución, sin margen de revendedor."},
            {icon:"✓",t:"Precio cerrado sin sorpresas",d:"El presupuesto incluye flete internacional, aranceles, IVA e IIBB. Lo que cotizamos es exactamente lo que pagás, entregado donde necesitás."},
            {icon:"→",t:"Carpintería y cocinas a medida",d:"Enviás tus planos, la fábrica emite shop drawings para tu aprobación antes de producción. Precio cerrado antes del primer corte."},
            {icon:"◉",t:"Un solo proveedor, todo incluido",d:"Sanitarios, pisos, puertas, cocinas y electrodomésticos en un despacho. Una factura, una coordinación, entrega donde necesitás."},
          ].map((b,i)=>(
            <Reveal key={i} delay={i*0.08} style={{background:"rgba(255,255,255,.04)",padding:"36px 32px",borderLeft:i%2===0?"none":`1px solid rgba(255,255,255,.07)`}}>
              <div style={{fontFamily:W.mono,fontSize:32,color:O,marginBottom:18,lineHeight:1}}>{b.icon}</div>
              <div style={{fontFamily:W.sans,fontSize:18,fontWeight:700,color:"#FAF9F7",marginBottom:12,lineHeight:1.25}}>{b.t}</div>
              <div style={{fontFamily:W.sans,fontSize:13,color:"rgba(255,255,255,.45)",lineHeight:1.85,fontWeight:300}}>{b.d}</div>
            </Reveal>
          ))}
        </div>
        <Reveal style={{marginTop:32,textAlign:"center"}}>
          <button style={{...BS,borderColor:"rgba(255,255,255,.15)",color:"rgba(255,255,255,.45)",fontSize:9,padding:"11px 28px"}}
            onClick={()=>navigate("/catalogo")}
            onMouseEnter={e=>{e.currentTarget.style.borderColor=O;e.currentTarget.style.color=O;}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor="rgba(255,255,255,.15)";e.currentTarget.style.color="rgba(255,255,255,.45)";}}>
            Ver catálogo completo →
          </button>
        </Reveal>
      </section>

      {/* ── CÓMO FUNCIONA ── */}
      <section id="como" className="pad" style={{padding:"80px 40px",background:W.s2}}>
        <Reveal style={{marginBottom:48}}>
          <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:8}}>
            <div style={{width:32,height:3,background:O}}/>
            <span style={{fontFamily:W.sans,fontSize:9,color:O,letterSpacing:2.5,textTransform:"uppercase",fontWeight:600}}>El proceso</span>
          </div>
          <h2 style={{fontFamily:W.sans,fontSize:"clamp(22px,3vw,40px)",fontWeight:800,color:BK}}>Cómo funciona</h2>
        </Reveal>
        <div className="steps-grid" style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:2,background:W.border}}>
          {[
            {n:"01",t:"Cotizás online",d:"Seleccionás productos. Precio final en USD con todos los costos incluidos, puesto en depósito Buenos Aires."},
            {n:"02",t:"Entrás al contenedor",d:"Tu pedido se consolida con el de otros compradores del mismo ciclo. El flete se divide proporcionalmente a tu volumen."},
            {n:"03",t:"Control en fábrica",d:"Nuestro equipo visita la fábrica, audita producción y aprueba cada ítem antes de embarcar. Ves fotos en tiempo real."},
            {n:"04",t:"Entrega en Argentina",d:"La mercadería llega a nuestro depósito en CABA. Te avisamos, coordinamos y facturamos exactamente tu parte."},
          ].map((s,i)=>(
            <Reveal key={i} delay={i*0.1} style={{background:W.s1,padding:"32px 24px"}}>
              <div style={{fontFamily:W.mono,fontSize:11,letterSpacing:2,color:O,marginBottom:20,fontWeight:500}}>{s.n}</div>
              <div style={{fontFamily:W.sans,fontSize:16,color:BK,marginBottom:12,fontWeight:700}}>{s.t}</div>
              <div style={{fontFamily:W.sans,fontSize:12,color:W.muted,lineHeight:1.8,fontWeight:300}}>{s.d}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── SECCIÓN CHINA — parallax ── */}
      <section style={{position:"relative",overflow:"hidden",minHeight:480,display:"flex",background:BK}}>
        <div style={{flex:1,position:"relative",overflow:"hidden",minHeight:480}}>
          <div style={{position:"absolute",inset:0,backgroundImage:"url('https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=900&q=80')",backgroundSize:"cover",backgroundPosition:"center",transform:`translateY(${(scrollY-2000)*0.12}px)`,willChange:"transform"}}/>
          <div style={{position:"absolute",inset:0,background:"linear-gradient(to right, rgba(15,13,12,.7), rgba(15,13,12,.2))"}}/>
          <div style={{position:"absolute",bottom:24,left:24,background:O,padding:"8px 16px"}}>
            <div style={{fontFamily:W.sans,fontSize:8,color:"#fff",letterSpacing:2.5,textTransform:"uppercase",fontWeight:700}}>Control en origen · Guangzhou</div>
          </div>
        </div>
        <Reveal style={{flex:1,padding:"56px 48px",display:"flex",flexDirection:"column",justifyContent:"center",borderLeft:`1px solid rgba(255,255,255,.08)`}}>
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
            <div style={{width:28,height:3,background:O}}/>
            <span style={{fontFamily:W.mono,fontSize:9,color:O,letterSpacing:2,textTransform:"uppercase"}}>Equipo propio en China</span>
          </div>
          <h2 style={{fontFamily:W.sans,fontSize:"clamp(20px,2.8vw,36px)",fontWeight:800,color:"#FAF9F7",lineHeight:1.15,marginBottom:20}}>
            Sin intermediarios.<br/><span style={{fontWeight:200,color:"rgba(255,255,255,.4)"}}>Precio de fábrica real.</span>
          </h2>
          <p style={{fontFamily:W.sans,fontSize:13,color:"rgba(255,255,255,.5)",lineHeight:1.85,marginBottom:28,fontWeight:300}}>
            Más de 40 años de relaciones directas en Guangdong. Visitamos proveedores, auditamos producción, aprobamos shop drawings y consolidamos el contenedor en persona.
          </p>
          <ul style={{listStyle:"none"}}>
            {["Auditoría de fábrica antes de cada pedido","Control de calidad pre-embarque","Shop drawings aprobados antes de producción","Seguimiento en tiempo real del embarque"].map(item=>(
              <li key={item} style={{fontFamily:W.sans,fontSize:12,color:"rgba(255,255,255,.55)",padding:"8px 0",borderBottom:`1px solid rgba(255,255,255,.08)`,display:"flex",alignItems:"center",gap:10,fontWeight:300}}>
                <span style={{color:O,fontWeight:700,fontSize:12,flexShrink:0}}>→</span>{item}
              </li>
            ))}
          </ul>
        </Reveal>
      </section>

      {/* ── TESTIMONIOS — rotating ── */}
      <section style={{padding:"80px 40px",background:W.bg}} className="pad">
        <Reveal style={{marginBottom:48}}>
          <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:8}}>
            <div style={{width:32,height:3,background:O}}/>
            <span style={{fontFamily:W.sans,fontSize:9,color:O,letterSpacing:2.5,textTransform:"uppercase",fontWeight:600}}>Clientes</span>
          </div>
          <h2 style={{fontFamily:W.sans,fontSize:"clamp(22px,3vw,40px)",fontWeight:800,color:BK}}>Lo que dicen</h2>
        </Reveal>

        {/* Testimonio principal rotante */}
        <div style={{position:"relative",overflow:"hidden",marginBottom:32}}>
          <div key={testIdx} style={{animation:"fadeUp .5s ease both"}}>
            <div style={{background:W.s1,border:`1.5px solid ${W.border}`,padding:"40px 48px",maxWidth:720,margin:"0 auto",position:"relative"}}>
              <div style={{fontFamily:W.mono,fontSize:60,color:O,opacity:.15,position:"absolute",top:20,left:24,lineHeight:1}}>"</div>
              <div style={{fontFamily:W.sans,fontSize:"clamp(15px,2vw,20px)",color:BK,lineHeight:1.75,marginBottom:28,fontWeight:300,position:"relative"}}>
                {TESTS[testIdx].q}
              </div>
              <div style={{display:"flex",alignItems:"center",gap:16}}>
                <div style={{width:32,height:2,background:O}}/>
                <div>
                  <div style={{fontFamily:W.sans,fontSize:13,color:BK,fontWeight:700}}>{TESTS[testIdx].a}</div>
                  <div style={{fontFamily:W.mono,fontSize:9,color:W.muted,marginTop:2}}>{TESTS[testIdx].o}</div>
                </div>
              </div>
            </div>
          </div>
          {/* Dots */}
          <div style={{display:"flex",justifyContent:"center",gap:8,marginTop:20}}>
            {TESTS.map((_,i)=>(
              <div key={i} onClick={()=>setTestIdx(i)}
                style={{width:i===testIdx?24:8,height:8,background:i===testIdx?O:W.border,borderRadius:4,cursor:"pointer",transition:"all .3s"}}/>
            ))}
          </div>
        </div>
      </section>

      {/* ── CONTACTO ── */}
      <section id="contacto" style={{background:BK}}>
        <div className="contact-grid" style={{display:"grid",gridTemplateColumns:"1.1fr 1fr",minHeight:600}}>
          {/* Left — imagen + copy */}
          <div style={{position:"relative",minHeight:500,overflow:"hidden"}}>
            <div style={{
              position:"absolute",inset:0,
              backgroundImage:"url('https://images.unsplash.com/photo-1556909172-54557c7e4fb7?w=900&q=80')",
              backgroundSize:"cover",backgroundPosition:"center",
              transform:`translateY(${(scrollY-3500)*0.08}px)`,
              willChange:"transform",
            }}/>
            <div style={{position:"absolute",inset:0,background:"linear-gradient(135deg,rgba(15,13,12,.9),rgba(15,13,12,.5))"}}/>
            <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",justifyContent:"flex-end",padding:48,color:"#fff"}}>
              <LogoWinarq height={38} dark={false}/>
              <div style={{marginTop:32}}>
                <div style={{fontFamily:W.mono,fontSize:9,color:O,letterSpacing:2,textTransform:"uppercase",marginBottom:14}}>Empezá tu cotización</div>
                <h2 style={{fontFamily:W.sans,fontSize:"clamp(24px,3vw,42px)",fontWeight:800,lineHeight:1.1,marginBottom:14}}>
                  Tu próxima obra,<br/><span style={{fontWeight:200,opacity:.5}}>al precio de fábrica.</span>
                </h2>
                <p style={{fontFamily:W.sans,fontSize:13,opacity:.5,lineHeight:1.8,maxWidth:340,marginBottom:28,fontWeight:300}}>
                  Completá el formulario y en 48 horas recibís el presupuesto detallado con todos los costos incluidos.
                </p>
                <a href="https://wa.me/5491150614489" target="_blank" rel="noreferrer"
                  style={{display:"inline-flex",alignItems:"center",gap:10,fontFamily:W.sans,fontSize:10,letterSpacing:2,textTransform:"uppercase",color:"#fff",textDecoration:"none",border:`1.5px solid ${O}`,padding:"10px 20px",fontWeight:700}}>
                  ↗ WhatsApp directo
                </a>
              </div>
              <div style={{marginTop:28,paddingTop:20,borderTop:"1px solid rgba(255,255,255,.1)"}}>
                <div style={{fontFamily:W.mono,fontSize:9,color:O,letterSpacing:1.5,textTransform:"uppercase",marginBottom:5}}>Depósito Buenos Aires</div>
                <div style={{fontFamily:W.sans,fontSize:12,opacity:.35,lineHeight:1.7,fontWeight:300}}>Batalla del Pari 1053, CABA<br/>Lunes a viernes 9 a 18 hs</div>
              </div>
            </div>
          </div>

          {/* Right — form */}
          {enviado ? (
            <div style={{background:W.bg,display:"flex",alignItems:"center",justifyContent:"center",padding:48}}>
              <div style={{textAlign:"center"}}>
                <div style={{fontSize:48,color:O,marginBottom:16,animation:"float 2s ease-in-out infinite"}}>✓</div>
                <div style={{fontFamily:W.sans,fontSize:24,color:BK,marginBottom:8,fontWeight:800}}>Solicitud enviada</div>
                <div style={{fontFamily:W.sans,fontSize:13,color:W.muted,lineHeight:1.7,fontWeight:300}}>Te contactamos en menos de 48 horas.</div>
              </div>
            </div>
          ) : (
            <div style={{padding:"48px 44px",background:W.bg}}>
              <div style={{fontFamily:W.sans,fontSize:22,color:BK,marginBottom:4,fontWeight:800}}>Cotización de obra</div>
              <div style={{fontFamily:W.sans,fontSize:12,color:W.muted,marginBottom:32,fontWeight:300}}>Respondemos en 48 horas con todos los costos incluidos.</div>

              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
                {[["Nombre","nombre","Arq. Juan García"],["Empresa","empresa","Estudio García"]].map(([l,k,ph])=>(
                  <div key={k}>
                    <div style={{fontFamily:W.sans,fontSize:8,letterSpacing:2,textTransform:"uppercase",color:W.muted,marginBottom:6,fontWeight:600}}>{l}</div>
                    <input value={form[k]||""} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))} placeholder={ph} style={INP}
                      onFocus={e=>e.target.style.borderBottomColor=O} onBlur={e=>e.target.style.borderBottomColor=W.border}/>
                  </div>
                ))}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
                {[["WhatsApp","whatsapp","+54 9 11 0000-0000"],["Email","email","juan@estudio.com"]].map(([l,k,ph])=>(
                  <div key={k}>
                    <div style={{fontFamily:W.sans,fontSize:8,letterSpacing:2,textTransform:"uppercase",color:W.muted,marginBottom:6,fontWeight:600}}>{l}</div>
                    <input value={form[k]||""} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))} placeholder={ph} style={INP}
                      onFocus={e=>e.target.style.borderBottomColor=O} onBlur={e=>e.target.style.borderBottomColor=W.border}/>
                  </div>
                ))}
              </div>
              <div style={{marginBottom:16}}>
                <div style={{fontFamily:W.sans,fontSize:8,letterSpacing:2,textTransform:"uppercase",color:W.muted,marginBottom:6,fontWeight:600}}>¿Qué rubros necesitás?</div>
                <select value={form.rubro} onChange={e=>setForm(p=>({...p,rubro:e.target.value}))}
                  style={{...INP,background:"#FFFFFF",border:`1px solid ${W.border}`,color:BK,padding:"8px 10px",WebkitAppearance:"menulist"}}>
                  {["Pisos SPC","Sanitarios / Bañeras / Vanitorys","Hornos y Anafes","Puertas WPC / MDF","Carpintería a medida","Cocinas integrales","Varios rubros"].map(o=><option key={o}>{o}</option>)}
                </select>
              </div>
              <div style={{marginBottom:32}}>
                <div style={{fontFamily:W.sans,fontSize:8,letterSpacing:2,textTransform:"uppercase",color:W.muted,marginBottom:6,fontWeight:600}}>Proyecto</div>
                <input value={form.proyecto||""} onChange={e=>setForm(p=>({...p,proyecto:e.target.value}))} placeholder="Torre 8 pisos, 20 unidades, Palermo" style={INP}
                  onFocus={e=>e.target.style.borderBottomColor=O} onBlur={e=>e.target.style.borderBottomColor=W.border}/>
              </div>
              <button style={{...BP,width:"100%",display:"block",textAlign:"center",padding:"14px 0"}} onClick={()=>setEnviado(true)}
                onMouseEnter={e=>e.currentTarget.style.background=OL}
                onMouseLeave={e=>e.currentTarget.style.background=O}>
                Solicitar cotización →
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{background:BK2,padding:"48px 40px 24px"}}>
        <div className="footer-grid" style={{display:"grid",gridTemplateColumns:"1.4fr 1fr 1fr 1fr",gap:40,marginBottom:40,paddingBottom:40,borderBottom:"1px solid rgba(255,255,255,.07)"}}>
          <div>
            <LogoWinarq height={38} dark={false}/>
            <div style={{fontFamily:W.sans,fontSize:11,color:"rgba(255,255,255,.3)",lineHeight:1.7,maxWidth:200,fontWeight:300,marginTop:16}}>
              Importación directa de materiales de construcción desde China para desarrolladores y arquitectos de Argentina.
            </div>
          </div>
          <div>
            <div style={{fontFamily:W.sans,fontSize:8,letterSpacing:2,textTransform:"uppercase",color:"rgba(255,255,255,.2)",marginBottom:16,fontWeight:600}}>Plataforma</div>
            {[["como","Cómo funciona"],["catalogo","Catálogo"],["precios","Por qué elegirnos"],["contacto","Cotizar obra"]].map(([id,l])=>(
              <div key={id} onClick={()=>go(id)} style={{fontFamily:W.sans,fontSize:12,color:"rgba(255,255,255,.35)",marginBottom:10,cursor:"pointer",fontWeight:300,transition:"color .2s"}}
                onMouseEnter={e=>e.target.style.color=O} onMouseLeave={e=>e.target.style.color="rgba(255,255,255,.35)"}>{l}</div>
            ))}
            <div onClick={()=>navigate("/auth")} style={{fontFamily:W.sans,fontSize:12,color:O,marginBottom:10,cursor:"pointer",fontWeight:700}}>Acceso clientes →</div>
          </div>
          <div>
            <div style={{fontFamily:W.sans,fontSize:8,letterSpacing:2,textTransform:"uppercase",color:"rgba(255,255,255,.2)",marginBottom:16,fontWeight:600}}>Productos</div>
            {["Sanitarios y bañeras","Pisos SPC","Hornos y Anafes","Puertas WPC","Carpintería","Cocinas integrales"].map(l=>(
              <div key={l} onClick={()=>navigate(`/catalogo?rubro=${encodeURIComponent(l)}`)} style={{fontFamily:W.sans,fontSize:12,color:"rgba(255,255,255,.35)",marginBottom:10,fontWeight:300,cursor:"pointer",transition:"color .2s"}}
                onMouseEnter={e=>e.target.style.color=O} onMouseLeave={e=>e.target.style.color="rgba(255,255,255,.35)"}>{l}</div>
            ))}
          </div>
          <div>
            <div style={{fontFamily:W.sans,fontSize:8,letterSpacing:2,textTransform:"uppercase",color:"rgba(255,255,255,.2)",marginBottom:16,fontWeight:600}}>Contacto</div>
            {[["BA","Buenos Aires\nBatalla del Pari 1053, CABA"],["CN","Guangzhou\nRepública Popular China"]].map(([c,a])=>(
              <div key={c} style={{display:"flex",gap:10,marginBottom:14,alignItems:"flex-start"}}>
                <span style={{fontFamily:W.mono,fontSize:8,color:O,flexShrink:0,paddingTop:2,width:20,fontWeight:600}}>{c}</span>
                <span style={{fontFamily:W.sans,fontSize:11,color:"rgba(255,255,255,.3)",lineHeight:1.6,whiteSpace:"pre-line",fontWeight:300}}>{a}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
          <div style={{fontFamily:W.mono,fontSize:9,color:"rgba(255,255,255,.18)"}}>© 2025 WINARQ · Importación directa China → Argentina</div>
          <a href="https://wa.me/5491150614489" target="_blank" rel="noreferrer"
            style={{fontFamily:W.sans,fontSize:9,letterSpacing:2,textTransform:"uppercase",color:O,textDecoration:"none",border:`1px solid ${O}`,padding:"7px 16px",fontWeight:700}}>
            ↗ WhatsApp
          </a>
        </div>
      </footer>
    </div>
  );
}
